/**
 * Every word the ICP fit says as a criterion — Story 20.9, DECISION-138.
 *
 * Kept apart by WHERE EACH WORD COMES FROM, as `extraction-copy.ts` does:
 *
 *   DRAWN     `design/screens/editor-qualify.html`'s `points` capture draws it —
 *             `Add a criterion` open on its third group, the closed control when
 *             there is nothing to match against, and the ideal customer match
 *             "once added". Held to the mockup by `tests/derived-copy.test.ts`.
 *   COMPOSED  no capture reaches it: the bench's sentence for a criterion that is
 *             unknown until a submission exists, and a refusal naming two.
 *
 * All of it is held to RULE-021: a criterion reads HOW CLOSELY a lead matches the
 * operator's own description, at points the operator sets — never whether a lead
 * is good.
 *
 * NO RUNTIME IMPORTS, so a node-side test can load this file without resolving
 * the workspace.
 */

export const DRAWN = {
  /** The criterion's name, as the door writes it. */
  label: 'Ideal customer match',
  /** Its source chip, in the same tint as `Extracted`. */
  chip: 'Derived',
  /** Under the row: what the number is. */
  describe: 'How closely a lead matches your description, from 0 to 100.',
  /** "Add a criterion"'s third group, and its one option. */
  group: 'From your ideal customer',
  option: 'How closely a lead matches',
  /** Beside "Add a criterion" when the ICP description is empty. */
  hint: 'Describe who you are looking for, and you can score how closely a lead matches.',
  /** The last row of its points: no fit was measured. */
  notMeasured: 'Not measured',
  scoreWhenNotMeasured: 'Score when not measured',
} as const

export const COMPOSED = {
  /**
   * The bench's reason a derived criterion is unknown. The drawn bench only
   * reasons about an extracted criterion ("is extracted and unknown until someone
   * writes something"); this is its sibling, in the lead drawer's own phrase for
   * a reading with no single sentence behind it — "read across the whole
   * submission".
   */
  benchWhy: (label: string): string => `${label} is read across the whole submission and unknown until someone answers`,
  /** The ICP card's refusal when two criteria read the fit — the drawing has one. */
  icpRefusedMany: (labels: string): string =>
    `${labels} score against this description. Remove those criteria first.`,
} as const
